import {
  useShippingMethod,
  useCreateShippingMethod,
  useUpdateShippingMethod,
} from '@/api';
import { Button, Input, Textarea } from '@/components/atoms';
import { FormField } from '@/components/molecules';
import {
  shippingCreationSchema,
  type ShippingCreationFormData,
} from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface ShippingCreationFormProps {
  id?: number;
  mode: 'create' | 'update';
  toggleOpen: () => void;
}

export const ShippingCreationForm = ({
  id,
  mode,
  toggleOpen,
}: ShippingCreationFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(shippingCreationSchema),
    mode: 'onChange',
  });

  const { data: shippingMethod } = useShippingMethod(
    mode === 'update' ? id : undefined
  );

  useEffect(() => {
    if (shippingMethod && mode === 'update') {
      setValue('location', shippingMethod.location);
      setValue('description', shippingMethod.description);
      setValue('fee', shippingMethod.fee);
      trigger();
    } else reset();
  }, [shippingMethod, mode, setValue, reset, trigger]);

  const { mutate: create, isPending } = useCreateShippingMethod(toggleOpen);

  const { mutate: update, isPending: isUpdatePending } =
    useUpdateShippingMethod(toggleOpen);

  const submit = async (shippingData: ShippingCreationFormData) => {
    if (mode === 'update' && id) {
      update({
        id,
        ...shippingData,
      });
    } else {
      create(shippingData);
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="grid grid-cols-2 gap-3">
      <FormField
        label="Location name / Shipping name"
        className="col-span-2"
        error={errors.location?.message}
      >
        <Input placeholder="eg. Ikeja" {...register('location')} />
      </FormField>
      <FormField
        label="Shipping description"
        className="col-span-2"
        error={errors.description?.message}
      >
        <Textarea
          placeholder="eg. Our shipping method ensures that all goods delivers fast."
          {...register('description')}
        />
      </FormField>
      <FormField
        label="Shipping fee"
        className="col-span-2"
        error={errors.fee?.message}
      >
        <Input placeholder="eg. 0.00" type="number" {...register('fee')} />
      </FormField>

      <Button
        className="col-span-2"
        label={`${mode === 'create' ? 'Create' : 'Update'} shipping method`}
        size="lg"
        isLoading={isPending || isUpdatePending}
        disabled={!isValid}
      />
    </form>
  );
};
